/* Shared empty-state styles (the `<EmptyState>` card). Scoped under `.nw-es`
 * with its own copy of the design tokens, same palette and theming convention
 * as sla.css / incidents.css / outlook.css: DARK is the default, and a host
 * that sets `.nw-theme-light` on an ancestor (the SPA's theme-init stamp, the
 * widget's theme class) flips the tokens via the block at EOF. Self-contained
 * so the component also works on SPA pages that load none of the panel CSS
 * (Backups, the conversations drawer). */

.nw-es {
  --card: #141b25;
  --line: rgba(255, 255, 255, 0.07);
  --ink: #e7ecf3;
  --soft: #98a4b4;
  --faint: #5e6b7c;
  --accent: #5b93e8;
  --green: #46b27e;
  --font-display: 'Saira', system-ui, sans-serif;
  --font-body: 'Barlow', system-ui, sans-serif;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 18px 26px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 14px;
  color: var(--ink);
  font-family: var(--font-body);
}
.nw-es * { box-sizing: border-box; }

/* --- illustration ---------------------------------------------------------
 * One 96×76 stroke scene per kind. The main stroke rides currentColor so the
 * variant tint below recolors the whole drawing; dim/accent strokes pull from
 * the tokens directly. */
.nw-es-art { width: 96px; height: 76px; margin-bottom: 12px; color: var(--soft); }
.nw-es-art * { fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.nw-es-art .ea { stroke: currentColor; }
.nw-es-art .ea-dim { stroke: var(--faint); }
.nw-es-art .ea-acc { stroke: var(--accent); }
.nw-es-art .ea-fill { fill: currentColor; fill-opacity: 0.07; }
.nw-es-art .ea-acc-fill { fill: var(--card); }
.nw-es-art .ea-dot { fill: currentColor; stroke: none; }
.nw-es-art .ea-dot.ea-acc { fill: var(--accent); }

/* variant tints: the good-news state reads green, everything else stays calm */
.nw-es--positive .nw-es-art { color: var(--green); }
.nw-es--positive .nw-es-art .ea-acc { stroke: var(--green); }
.nw-es--positive .nw-es-art .ea-dot.ea-acc { fill: var(--green); stroke: none; }

.nw-es-t { font-family: var(--font-display); font-size: 15px; font-weight: 600; color: var(--ink); }
.nw-es-s { font-size: 13px; color: var(--soft); margin-top: 4px; max-width: 400px; line-height: 1.5; }

.nw-es-cta {
  margin-top: 14px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.nw-es-cta:hover { filter: brightness(1.08); }

/* --- compact: for in-panel / overlay spots where a full card is too loud --- */
.nw-es--compact { padding: 12px 8px; background: transparent; border: none; }
.nw-es--compact .nw-es-art { width: 58px; height: 46px; margin-bottom: 6px; }
.nw-es--compact .nw-es-t { font-size: 13.5px; }
.nw-es--compact .nw-es-s { font-size: 12.5px; }

/* --- LIGHT theme (host sets `.nw-theme-light` on an ancestor) ------------- */
.nw-theme-light .nw-es {
  --card: #ffffff;
  --line: rgba(17, 24, 39, 0.10);
  --ink: #111827;
  --soft: #566069;
  --faint: #828b99;
  --accent: #2f6fd0;
}
